import React, { useEffect, useState, useRef, useMemo } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  useReactFlow,
  ReactFlowProvider,
  Position,
  EdgeLabelRenderer,
  getBezierPath,
} from 'reactflow';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  Divider,
  Button,
  Menu,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  Drawer,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Fab,
  Stack,
  Chip,
} from '@mui/material';
import 'reactflow/dist/style.css';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { ListItemIcon } from '@mui/material';

import {
  CameraAlt,
  Refresh,
  Visibility,
  Menu as MenuIcon,
  Close as CloseIcon,
  FilterList,
  ViewCompact,
  ViewList,
  AccountTree,
  Layers,
  Label,
} from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states/auth.js';
import { useKnowledgeMapSubjects } from '../Sidebar/ConceptBuilderSideBar.tsx';

const LEVEL_GAP_X = 600;
const VERTICAL_GAP = 120;

// Import your actual services
import { AiActions } from '../../Services/_actions/Ai/ai.actions.ts';
import { useLocation, useNavigate } from 'react-router';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import SearchableDropdown from '../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { ConstantActions } from '../../Services/_actions/constant.actions.js';
import { globalpermissions } from '../../Services/Permission.js';
import { exportFlowToPdf } from '../Shared/FlowToPdf/exportFlowToPdf.tsx';
import OrganisationDropdown from '../Shared/OrganisationDropdown/index.tsx';
import { exportTableToPdf } from './exportTableToPdf.tsx';
import KnowledgeMapTable from './KnowledgeMapTable.tsx';
import TableViewIcon from '@mui/icons-material/TableView';

const topicColors = [
  '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981',
  '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
];

const mixColors = (color1, color2) => {
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');

  const r1 = parseInt(hex1.substr(0, 2), 16);
  const g1 = parseInt(hex1.substr(2, 2), 16);
  const b1 = parseInt(hex1.substr(4, 2), 16);

  const r2 = parseInt(hex2.substr(0, 2), 16);
  const g2 = parseInt(hex2.substr(2, 2), 16);
  const b2 = parseInt(hex2.substr(4, 2), 16);

  const r = Math.round((r1 + r2) / 2);
  const g = Math.round((g1 + g2) / 2);
  const b = Math.round((b1 + b2) / 2);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// ============================================================
// CUSTOM EDGE WITH LABEL
// ============================================================
const CustomEdge = ({ id, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, data, markerEnd }) => {
  const edgePathParams = {
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  };

  const [edgePath, labelX, labelY] = getBezierPath(edgePathParams);

  const labelColor = data?.labelColor || '#64748b';
  const labelText = data?.label || '';

  return (
    <>
      <path
        id={id}
        style={style}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />
      {labelText && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              background: 'white',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '9px',
              fontWeight: 600,
              color: labelColor,
              border: `1px solid ${labelColor}40`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              pointerEvents: 'all',
              whiteSpace: 'nowrap',
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            className="nodrag nopan"
          >
            {labelText}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const FlowComponent = () => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const location = useLocation();
  const constantActions = ConstantActions();
  const theme = useTheme();

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const useCompactZoom = isMobile || isDesktop;

  // ============================================================
  // STATE
  // ============================================================
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [allNodes, setAllNodes] = useState([]);
  const [allEdges, setAllEdges] = useState([]);
  const [curriculumData, setCurriculumData] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [sidePanelContent, setSidePanelContent] = useState(null);
  const [aiActions] = useState(AiActions());
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl1, setAnchorEl1] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dataToDelete, setDataToDelete] = useState(null);
  const [error, setError] = useState('');
  const [availableClasses, setAvailableClasses] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showFullView, setShowFullView] = useState(true);
  const loggedInUser = useRecoilValue(userInformationAtom);
  const loggedInUsername = loggedInUser?.username || '';
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const [showTableView, setShowTableView] = useState(false);
  const tableRef = useRef(null);
  const isSuperUser = loggedInUser?.is_superuser === true;
  const hasClassListPermission = isSuperUser || constantActions.userHasPermission([globalpermissions?.performancePermissions?.classListAccess]);

  // Collapsible mode state
  const [isCollapsibleMode, setIsCollapsibleMode] = useState(false);
  const [expandedNodeId, setExpandedNodeId] = useState(null);
  const [expansionHistory, setExpansionHistory] = useState([]);

  const {
    entityData: studentEntityData,
  } = useKnowledgeMapSubjects(true, undefined, { fetchKnowledgeMap: false });

  useEffect(() => {
    if (hasClassListPermission) return;
    if (!studentEntityData?.items?.length || !loggedInUsername) return;
    studentEntityData.items.forEach((item: any) => {
      if (item?.entity?.username?.toLowerCase() === loggedInUsername.toLowerCase()) {
        const studentClass = item.entity.class;
        if (studentClass) {
          setSelectedClass(String(studentClass));
        }
      }
    });
  }, [studentEntityData, loggedInUsername, isSuperUser]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);

  const levelSpacing = 110;
  const reactFlowWrapper = useRef(null);
  const { fitView } = useReactFlow();

  const handleMenuOpen = (event, nodeId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleDownloadOpen = (event, nodeId) => {
    event.stopPropagation();
    setAnchorEl1(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setAnchorEl1(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (location.state?.selectedSubject) {
      setSelectedSubject(location.state.selectedSubject);
      setSelectedClass(location.state.selectedClass);
    }
  }, [location.state]);

  const handleEdit = () => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );
    if (currentData) {
      navigate(`/r/student/edit/${currentData._id}`, {
        state: { subjectData: currentData },
      });
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );
    setDataToDelete(currentData);
    setConfirmOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async (confirmed) => {
    if (!confirmed) {
      setConfirmOpen(false);
      return;
    }

    if (dataToDelete && dataToDelete._id) {
      try {
        await aiActions.deleteKnowledge(dataToDelete._id);
        setCurriculumData(curriculumData.filter((c) => c._id !== dataToDelete._id));
        setNodes([]);
        setEdges([]);
        setAllNodes([]);
        setAllEdges([]);
        setSelectedClass('');
        setSelectedSubject('');
        setSidePanelContent(null);
        showSnackBar('Deleted successfully!', 'success');
        setError('');
      } catch (error) {
        showSnackBar('Failed to delete. Please try again.', 'error');
        setError('Failed to delete. Please try again.');
      }
    }
    setConfirmOpen(false);
  };

  // Load curriculum data from API with organisation filter
  useEffect(() => {
    if (hasClassListPermission && !selectedOrganisation) {
      setCurriculumData([]);
      setAvailableClasses([]);
      return;
    }
    
    const fetchData = async () => {
      try {
        const organisationId = hasClassListPermission ? selectedOrganisation?.organisation_id : undefined;
        const data = await aiActions.getKnowledgeList(organisationId);
        setCurriculumData(data);
        const classes = [...new Set(data.map((d) => d.class))].sort();
        setAvailableClasses(classes);
      } catch (err) {
        console.error(err);
        setError('Failed to load curriculum data');
      }
    };
    fetchData();
  }, [selectedOrganisation, hasClassListPermission, aiActions]);

  useEffect(() => {
    if (selectedClass) {
      const subjects = curriculumData
        .filter((d) => String(d.class) === String(selectedClass))
        .map((d) => d.subject);
      setAvailableSubjects([...new Set(subjects)]);
    } else {
      setAvailableSubjects([]);
    }
  }, [selectedClass, curriculumData]);

  // Build the flow when both class and subject are selected
  useEffect(() => {
    if (selectedClass && selectedSubject && curriculumData.length > 0) {
      if (isCollapsibleMode) {
        buildCollapsibleFlow();
      } else {
        buildFlow();
      }
    } else {
      setNodes([]);
      setEdges([]);
      setAllNodes([]);
      setAllEdges([]);
      setSidePanelContent(null);
      setExpandedNodeId(null);
      setExpansionHistory([]);
    }
  }, [selectedClass, selectedSubject, curriculumData, isCollapsibleMode]);

  const getSubtopicColor = (subtopicName, topicColorMap, subtopicParents) => {
    const parents = subtopicParents[subtopicName] || [];

    if (parents.length === 0) {
      return topicColorMap[0];
    } else if (parents.length === 1) {
      return topicColorMap[parents[0]];
    } else if (parents.length === 2) {
      return mixColors(topicColorMap[parents[0]], topicColorMap[parents[1]]);
    } else {
      return mixColors(topicColorMap[parents[0]], topicColorMap[parents[1]]);
    }
  };

  // Helper function to flatten nested prerequisites recursively
  const flattenPrerequisites = (prereqObj, parentId = null, level = 0) => {
    const result = [];

    if (prereqObj && typeof prereqObj === 'object' && prereqObj.name) {
      result.push({
        id: prereqObj.id,
        name: prereqObj.name,
        parentId: parentId,
        level: level,
        hasChildren: prereqObj.children && prereqObj.children.length > 0
      });

      if (prereqObj.children && prereqObj.children.length > 0) {
        prereqObj.children.forEach(child => {
          result.push(...flattenPrerequisites(child, prereqObj.id, level + 1));
        });
      }
    }

    return result;
  };

  const estimateHeight = (label) => {
    const base = 50;
    const perLine = 20;
    const charsPerLine = 20;

    const lines = Math.ceil(label.length / charsPerLine);
    return base + (lines - 1) * perLine;
  };

  /**
   * Calculate positions with NO OVERLAPPING using a grid-based approach
   */
  const calculateNonOverlappingPositions = (nodesToLayout, edgesToLayout) => {
  if (nodesToLayout.length === 0) return nodesToLayout;

  const layoutNodes = [...nodesToLayout];
  
  // Group nodes by level
  const nodesByLevel = {};
  layoutNodes.forEach(node => {
    const level = node.data.level || 0;
    if (!nodesByLevel[level]) nodesByLevel[level] = [];
    nodesByLevel[level].push(node);
  });

  // Get sorted levels
  const sortedLevels = Object.keys(nodesByLevel).sort((a, b) => Number(a) - Number(b));
  
  const NODE_WIDTH = 200;        // Increase if nodes are wider
  const NODE_HEIGHT = 60;        // Increase if nodes are taller
  const HORIZONTAL_SPACING = 350; // Increase for more horizontal space between levels (was 320)
  const VERTICAL_SPACING = 120;   // Increase for more vertical space between nodes (was 100)
  const START_X = 80;
  const START_Y = 80;

  // Calculate positions level by level
  sortedLevels.forEach((levelKey, levelIndex) => {
    const level = Number(levelKey);
    const nodesAtLevel = nodesByLevel[levelKey];
    const xPos = START_X + (levelIndex * HORIZONTAL_SPACING);
    
    // Sort nodes by their current Y position or by ID for consistency
    nodesAtLevel.sort((a, b) => {
      const yA = a.position?.y || 0;
      const yB = b.position?.y || 0;
      return yA - yB;
    });

    // Calculate total height needed
    const totalNodes = nodesAtLevel.length;
    const totalHeight = Math.max((totalNodes - 1) * VERTICAL_SPACING, VERTICAL_SPACING);
    const startYPos = START_Y + (levelIndex * 20) - totalHeight / 2;

    // Position each node
    nodesAtLevel.forEach((node, idx) => {
      let yPos = startYPos + (idx * VERTICAL_SPACING);
      
      // Ensure minimum spacing from other levels
      let attempts = 0;
      while (attempts < 50) {
        let overlapping = false;
        
        // Check against all nodes in other levels
        for (const otherLevelKey of sortedLevels) {
          if (otherLevelKey === levelKey) continue;
          const otherNodes = nodesByLevel[otherLevelKey] || [];
          for (const otherNode of otherNodes) {
            if (!otherNode.position) continue;
            const dx = Math.abs(xPos - otherNode.position.x);
            const dy = Math.abs(yPos - otherNode.position.y);
            // Increase the overlap detection threshold for more spacing
            if (dx < NODE_WIDTH + 60 && dy < NODE_HEIGHT + 40) {
              overlapping = true;
              break;
            }
          }
          if (overlapping) break;
        }
        
        if (!overlapping) break;
        
        // Move down and try again
        yPos += 40; // Increased from 30 for more aggressive spacing
        attempts++;
      }
      
      node.position = {
        x: xPos,
        y: yPos
      };
      node.sourcePosition = Position.Right;
      node.targetPosition = Position.Left;
    });
  });

  // Final pass: ensure no overlaps between any nodes
  for (let i = 0; i < layoutNodes.length; i++) {
    for (let j = i + 1; j < layoutNodes.length; j++) {
      const nodeA = layoutNodes[i];
      const nodeB = layoutNodes[j];
      if (!nodeA.position || !nodeB.position) continue;
      
      const dx = Math.abs(nodeA.position.x - nodeB.position.x);
      const dy = Math.abs(nodeA.position.y - nodeB.position.y);
      
      if (dx < NODE_WIDTH + 50 && dy < NODE_HEIGHT + 40) {
        // Push node B down
        nodeB.position.y += 50; // Increased from 40
        // Recursively check if this causes new overlaps
        j = i; // Reset inner loop
      }
    }
  }

  return layoutNodes;
};


  // ============================================================
  // FULL VIEW MODE FUNCTIONS
  // ============================================================

  const relayoutFilteredNodes = (nodesToLayout, clickedNode) => {
    const layoutNodes = [...nodesToLayout];
    const subtopicNode = layoutNodes.find(n => n.id === clickedNode.id);

    if (!subtopicNode) return layoutNodes;

    const startX = 80;
    const startY = 300;
    subtopicNode.position = { x: startX, y: startY };

    const prereqsByLevel = {};
    layoutNodes.forEach(n => {
      if (n.data.type === 'prerequisite') {
        const level = n.data.level || 0;
        if (!prereqsByLevel[level]) prereqsByLevel[level] = [];
        prereqsByLevel[level].push(n);
      }
    });

    const NODE_HEIGHT = 60;
    const VERTICAL_SPACING = 100;
    const HORIZONTAL_SPACING = 350;

    Object.keys(prereqsByLevel).forEach(levelKey => {
      const level = parseInt(levelKey);
      const prereqs = prereqsByLevel[level];
      const xOffset = startX + 350 + (level * HORIZONTAL_SPACING);
      
      prereqs.sort((a, b) => a.data.label.localeCompare(b.data.label));
      
      const totalHeight = Math.max((prereqs.length - 1) * VERTICAL_SPACING, VERTICAL_SPACING);
      const startYForLevel = startY - totalHeight / 2;

      prereqs.forEach((prereq, idx) => {
        let yPos = startYForLevel + idx * VERTICAL_SPACING;
        
        let attempts = 0;
        while (attempts < 30) {
          let overlapping = false;
          for (const otherLevelKey of Object.keys(prereqsByLevel)) {
            if (otherLevelKey === levelKey) continue;
            const otherPrereqs = prereqsByLevel[otherLevelKey] || [];
            for (const other of otherPrereqs) {
              if (!other.position) continue;
              const dx = Math.abs(xOffset - other.position.x);
              const dy = Math.abs(yPos - other.position.y);
              if (dx < 200 && dy < NODE_HEIGHT + 20) {
                overlapping = true;
                break;
              }
            }
            if (overlapping) break;
          }
          if (!overlapping) break;
          yPos += 30;
          attempts++;
        }
        
        prereq.position = {
          x: xOffset,
          y: yPos
        };
      });
    });

    return layoutNodes;
  };

  const relayoutAllNodes = (nodesToLayout) => {
    return calculateNonOverlappingPositions(nodesToLayout, []);
  };

  // ============================================================
  // COLLAPSIBLE MODE FUNCTIONS - FIXED
  // ============================================================

  /**
   * Get ONLY direct children of a node (not all descendants)
   * This ensures clicking Subject only shows Topics, not the complete map
   */
  const getDirectChildren = (nodeId, allEdgesList) => {
    const children = [];
    allEdgesList.forEach(edge => {
      if (edge.source === nodeId) {
        children.push(edge.target);
      }
    });
    return children;
  };

  /**
   * Get visible nodes for collapsible mode - ONLY shows direct children
   */
  const getVisibleNodes = (allNodesList, allEdgesList, expandedId, history) => {
    // If nothing is expanded, show only the root (Subject)
    if (!expandedId && history.length === 0) {
      const rootNode = allNodesList.find(n => n.data.type === 'subject' || n.data.level === 0);
      if (rootNode) {
        return {
          nodes: [rootNode],
          edges: []
        };
      }
      return { nodes: [], edges: [] };
    }

    const visibleIds = new Set();
    
    // Always include all ancestor nodes from history
    history.forEach(id => visibleIds.add(id));
    
    // Include the expanded node
    if (expandedId) {
      visibleIds.add(expandedId);
    }

    // IMPORTANT: Only add DIRECT children of the expanded node
    if (expandedId) {
      const directChildren = getDirectChildren(expandedId, allEdgesList);
      directChildren.forEach(childId => {
        visibleIds.add(childId);
      });
    }

    // Also add direct children of all ancestors (to show siblings)
    history.forEach(ancestorId => {
      const directChildren = getDirectChildren(ancestorId, allEdgesList);
      directChildren.forEach(childId => {
        visibleIds.add(childId);
      });
    });

    // Filter nodes
    const visibleNodes = allNodesList.filter(n => visibleIds.has(n.id));
    
    // Filter edges (only between visible nodes)
    const visibleEdges = allEdgesList.filter(
      edge => visibleIds.has(edge.source) && visibleIds.has(edge.target)
    );

    return { nodes: visibleNodes, edges: visibleEdges };
  };

  const layoutCollapsibleNodes = (nodesToLayout, edgesToLayout) => {
    return calculateNonOverlappingPositions(nodesToLayout, edgesToLayout);
  };

  const updateCollapsibleView = (expandedId, history) => {
    const { nodes: visibleNodes, edges: visibleEdges } = getVisibleNodes(
      allNodes,
      allEdges,
      expandedId,
      history
    );

    const layoutedNodes = layoutCollapsibleNodes(visibleNodes, visibleEdges);
    
    setNodes(layoutedNodes);
    setEdges(visibleEdges);
    setSelectedNode(expandedId);

    setTimeout(() => fitView({ padding: 0.3, duration: 400 }), 100);
  };

  // ============================================================
  // BUILD COLLAPSIBLE FLOW
  // ============================================================

  const buildCollapsibleFlow = () => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );

    if (!currentData || !currentData.topics) {
      setNodes([]);
      setEdges([]);
      setAllNodes([]);
      setAllEdges([]);
      return;
    }

    const newNodes = [];
    const newEdges = [];
    const topics = currentData.topics;

    // Add Subject node (Level 0)
    const subjectNode = {
      id: 'subject_root',
      data: {
        label: `${selectedSubject}`,
        type: 'subject',
        level: 0,
        subjectData: currentData,
      },
      position: { x: 50, y: 300 },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      style: {
        background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)',
        border: '3px solid #C0392B',
        borderRadius: '20px',
        padding: '18px 28px',
        fontSize: '18px',
        fontWeight: '700',
        color: '#ffffff',
        cursor: 'pointer',
        boxShadow: '0 6px 24px rgba(231, 76, 60, 0.4)',
        minWidth: '180px',
        textAlign: 'center',
      },
    };
    newNodes.push(subjectNode);

    // Assign colors to topics
    const topicColorMap = {};
    topics.forEach((topic, idx) => {
      topicColorMap[idx] = topicColors[idx % topicColors.length];
    });

    // Build subtopic to parent topics mapping
    const subtopicParents = {};
    const subtopicData = {};

    topics.forEach((topic, topicIdx) => {
      // Add Topic nodes (Level 1)
      const topicNode = {
        id: `topic_${topicIdx}`,
        data: {
          label: topic.topic,
          type: 'topic',
          level: 1,
          index: topicIdx,
          topicData: topic,
        },
        position: { x: 350 + (topicIdx * 20), y: 100 + (topicIdx * 100) },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: `linear-gradient(135deg, ${topicColorMap[topicIdx]} 0%, ${topicColorMap[topicIdx]}dd 100%)`,
          border: `3px solid ${topicColorMap[topicIdx]}`,
          borderRadius: '16px',
          padding: '12px 20px',
          fontSize: '14px',
          fontWeight: '600',
          color: '#ffffff',
          cursor: 'pointer',
          boxShadow: `0 4px 16px ${topicColorMap[topicIdx]}66`,
          minWidth: '140px',
          textAlign: 'center',
        },
      };
      newNodes.push(topicNode);

      // Connect Subject to Topic
      newEdges.push({
        id: `subject-topic_${topicIdx}`,
        source: 'subject_root',
        target: `topic_${topicIdx}`,
        type: 'custom',
        data: {
          label: `Topic ${topicIdx + 1}`,
          labelColor: topicColorMap[topicIdx],
        },
        style: {
          stroke: topicColorMap[topicIdx],
          strokeWidth: 2,
        },
      });

      topic.subtopics?.forEach((st) => {
        if (!subtopicParents[st.subtopic]) {
          subtopicParents[st.subtopic] = [];
          subtopicData[st.subtopic] = st;
        }
        subtopicParents[st.subtopic].push(topicIdx);
      });
    });

    // Get all unique subtopics with proper spacing
    const subtopicList = Object.keys(subtopicParents).sort((a, b) => {
      const aFirstParent = subtopicParents[a][0];
      const bFirstParent = subtopicParents[b][0];
      return aFirstParent - bFirstParent;
    });

    let currentY = 100;
    const subtopicYPositions = {};

    subtopicList.forEach((subtopicName) => {
      const label = subtopicName;
      const heightNeeded = estimateHeight(label);
      subtopicYPositions[subtopicName] = currentY;
      currentY += heightNeeded + 120;
    });

    // CREATE SUBTOPIC NODES (Level 2)
    subtopicList.forEach((subtopicName) => {
      const color = getSubtopicColor(subtopicName, topicColorMap, subtopicParents);
      const nodeId = `subtopic_${subtopicName.replace(/\s+/g, '_')}`;
      const parentTopicIndex = subtopicParents[subtopicName][0];
      const parentTopicName = topics[parentTopicIndex].topic;
      
      const subtopicNode = {
        id: nodeId,
        data: {
          label: subtopicName,
          type: 'subtopic',
          level: 2,
          fullData: subtopicData[subtopicName],
          parents: subtopicParents[subtopicName],
          parentTopic: parentTopicName,
        },
        position: { x: 700, y: subtopicYPositions[subtopicName] },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
          border: `3px solid ${color}`,
          borderRadius: '16px',
          padding: '12px 18px',
          fontSize: '13px',
          fontWeight: '600',
          color: '#ffffff',
          cursor: 'pointer',
          boxShadow: `0 4px 16px ${color}66`,
          minWidth: '150px',
          textAlign: 'center',
        },
      };
      newNodes.push(subtopicNode);

      // Connect Topic to Subtopic
      subtopicParents[subtopicName].forEach((topicIdx) => {
        const edgeColor = topicColorMap[topicIdx];
        const topicName = topics[topicIdx].topic;
        
        newEdges.push({
          id: `topic_${topicIdx}-${nodeId}`,
          source: `topic_${topicIdx}`,
          target: nodeId,
          type: 'custom',
          data: {
            label: `→ ${topicName}`,
            labelColor: edgeColor,
          },
          style: {
            stroke: edgeColor,
            strokeWidth: 2,
          },
        });
      });
    });

    // FLATTEN ALL PREREQUISITES (Levels 3 and 4)
    const allPrerequisitesMap = new Map();
    const prerequisiteRelationships = new Map();
    const subtopicToPrereqMap = new Map();

    Object.entries(subtopicData).forEach(([subtopicName, data]) => {
      if (data.prerequisites && Array.isArray(data.prerequisites)) {
        data.prerequisites.forEach(prereq => {
          const flattened = flattenPrerequisites(prereq);
          flattened.forEach(flatPrereq => {
            if (!allPrerequisitesMap.has(flatPrereq.id)) {
              allPrerequisitesMap.set(flatPrereq.id, {
                ...flatPrereq,
                connectedSubtopics: new Set()
              });
            }
            if (flatPrereq.level === 0) {
              allPrerequisitesMap.get(flatPrereq.id).connectedSubtopics.add(subtopicName);
              if (!subtopicToPrereqMap.has(subtopicName)) {
                subtopicToPrereqMap.set(subtopicName, []);
              }
              subtopicToPrereqMap.get(subtopicName).push(flatPrereq.id);
            }

            if (flatPrereq.parentId) {
              if (!prerequisiteRelationships.has(flatPrereq.parentId)) {
                prerequisiteRelationships.set(flatPrereq.parentId, []);
              }
              prerequisiteRelationships.get(flatPrereq.parentId).push(flatPrereq.id);
            }
          });
        });
      }
    });

    let levelTracker = {};
    const levelXOffset = 450;
    const prerequisitePositions = new Map();

    const sortedPrereqs = Array.from(allPrerequisitesMap.entries())
      .sort((a, b) => {
        if (a[1].level !== b[1].level) return a[1].level - b[1].level;
        return 0;
      });

    sortedPrereqs.forEach(([prereqId, prereqData]) => {
      const baseX = 1050;
      const xPos = baseX + (prereqData.level * levelXOffset);

      let siblingCount = prerequisiteRelationships.get(prereqData.parentId)?.length || 1;
      let dynamicGap = 120 + (siblingCount * 30);

      if (levelTracker[prereqData.level] == null) {
        levelTracker[prereqData.level] = 100;
      }
      let yPos = levelTracker[prereqData.level];
      levelTracker[prereqData.level] += dynamicGap;

      prerequisitePositions.set(prereqId, { x: xPos, y: yPos });

      let prereqColor = '#4ade80';
      const connectedSubtopics = Array.from(prereqData.connectedSubtopics);
      if (connectedSubtopics.length > 0) {
        prereqColor = getSubtopicColor(connectedSubtopics[0], topicColorMap, subtopicParents);
      }

      const opacity = Math.max(0.3, 1 - (prereqData.level * 0.15));
      const borderWidth = Math.max(1, 2 - prereqData.level);
      const level = prereqData.level + 3;

      const prereqNode = {
        id: `prereq_${prereqId}`,
        data: {
          label: prereqData.name,
          type: 'prerequisite',
          level: level,
          prerequisiteData: prereqData,
        },
        position: { x: xPos, y: yPos },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: `linear-gradient(135deg, ${prereqColor}${Math.round(opacity * 100).toString(16).padStart(2, '0')} 0%, ${prereqColor}${Math.round(opacity * 50).toString(16).padStart(2, '0')} 100%)`,
          border: `${borderWidth}px solid ${prereqColor}`,
          borderRadius: '12px',
          padding: prereqData.level === 0 ? '10px 16px' : '8px 12px',
          fontSize: prereqData.level === 0 ? '12px' : '11px',
          fontWeight: '600',
          color: prereqData.level === 0 ? '#1a1a1a' : '#4a5568',
          cursor: 'pointer',
          boxShadow: `0 4px 12px ${prereqColor}${Math.round(opacity * 64).toString(16).padStart(2, '0')}`,
          minWidth: prereqData.level === 0 ? '130px' : '110px',
          textAlign: 'center',
        },
      };
      newNodes.push(prereqNode);

      // Connect Subtopic to Prerequisite
      if (prereqData.level === 0) {
        connectedSubtopics.forEach(subtopicName => {
          const subtopicId = `subtopic_${subtopicName.replace(/\s+/g, '_')}`;
          const edgeColor = getSubtopicColor(subtopicName, topicColorMap, subtopicParents);
          
          const parentTopicNames = subtopicParents[subtopicName]?.map(idx => topics[idx].topic) || [];
          const parentTopicName = parentTopicNames[0] || '';

          newEdges.push({
            id: `${subtopicId}-prereq_${prereqId}`,
            source: subtopicId,
            target: `prereq_${prereqId}`,
            type: 'custom',
            data: {
              label: `📖 ${parentTopicName || subtopicName}`,
              labelColor: edgeColor,
            },
            style: {
              stroke: edgeColor,
              strokeWidth: 2,
              strokeDasharray: '5,5',
            },
          });
        });
      }
    });

    // Connect Prerequisites to Pre-prerequisites
    prerequisiteRelationships.forEach((childIds, parentId) => {
      childIds.forEach(childId => {
        const parentData = allPrerequisitesMap.get(parentId);
        let edgeColor = '#94a3b8';
        let labelText = '→';

        if (parentData && parentData.connectedSubtopics.size > 0) {
          const firstSubtopic = Array.from(parentData.connectedSubtopics)[0];
          edgeColor = getSubtopicColor(firstSubtopic, topicColorMap, subtopicParents);
          
          const parentPrereq = allPrerequisitesMap.get(parentId);
          const childPrereq = allPrerequisitesMap.get(childId);
          
          if (parentPrereq && childPrereq) {
            labelText = `${parentPrereq.name} → ${childPrereq.name}`;
          }
        }

        newEdges.push({
          id: `prereq_${parentId}-prereq_${childId}`,
          source: `prereq_${parentId}`,
          target: `prereq_${childId}`,
          type: 'custom',
          data: {
            label: labelText.length > 30 ? labelText.substring(0, 27) + '...' : labelText,
            labelColor: edgeColor,
          },
          style: {
            stroke: edgeColor,
            strokeWidth: 1.5,
            strokeDasharray: '3,3',
          },
        });
      });
    });

    setAllNodes(newNodes);
    setAllEdges(newEdges);
    
    // Start with only Subject visible
    setExpandedNodeId(null);
    setExpansionHistory([]);
    setShowFullView(false);
    setSelectedNode(null);
    
    // Show only the subject node initially
    const rootNode = newNodes.find(n => n.data.type === 'subject' || n.data.level === 0);
    if (rootNode) {
      setNodes([rootNode]);
      setEdges([]);
    } else {
      setNodes([]);
      setEdges([]);
    }

    setTimeout(() => fitView({ padding: 0.5, duration: 400 }), 100);
  };

  // ============================================================
  // FULL VIEW MODE - BUILD FLOW
  // ============================================================

  const buildFlow = () => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );

    if (!currentData || !currentData.topics) {
      setNodes([]);
      setEdges([]);
      setAllNodes([]);
      setAllEdges([]);
      return;
    }

    const newNodes = [];
    const newEdges = [];
    const topics = currentData.topics;

    // Assign colors to topics
    const topicColorMap = {};
    topics.forEach((topic, idx) => {
      topicColorMap[idx] = topicColors[idx % topicColors.length];
    });

    // Build subtopic to parent topics mapping
    const subtopicParents = {};
    const subtopicData = {};

    topics.forEach((topic, topicIdx) => {
      topic.subtopics?.forEach((st) => {
        if (!subtopicParents[st.subtopic]) {
          subtopicParents[st.subtopic] = [];
          subtopicData[st.subtopic] = st;
        }
        subtopicParents[st.subtopic].push(topicIdx);
      });
    });

    // Get all unique subtopics with proper spacing
    const subtopicList = Object.keys(subtopicParents).sort((a, b) => {
      const aFirstParent = subtopicParents[a][0];
      const bFirstParent = subtopicParents[b][0];
      return aFirstParent - bFirstParent;
    });

    let currentY = 100;
    const subtopicYPositions = {};

    subtopicList.forEach((subtopicName) => {
      const label = subtopicName;
      const heightNeeded = estimateHeight(label);
      subtopicYPositions[subtopicName] = currentY;
      currentY += heightNeeded + 120;
    });

    // CREATE SUBTOPIC NODES (Level 0 in Full View Mode)
    subtopicList.forEach((subtopicName) => {
      const color = getSubtopicColor(subtopicName, topicColorMap, subtopicParents);
      const nodeId = `subtopic_${subtopicName.replace(/\s+/g, '_')}`;
      const parentTopicIndices = subtopicParents[subtopicName];
      const parentTopicNames = parentTopicIndices.map(idx => topics[idx].topic);
      
      let displayLabel = subtopicName;
      if (parentTopicNames.length === 1) {
        displayLabel = `${subtopicName} (${parentTopicNames[0]})`;
      } else if (parentTopicNames.length > 1) {
        displayLabel = `${subtopicName} (${parentTopicNames.join(', ')})`;
      }
      
      const subtopicNode = {
        id: nodeId,
        data: {
          label: displayLabel,
          type: 'subtopic',
          level: 0,
          fullData: subtopicData[subtopicName],
          parents: parentTopicIndices,
          parentTopic: parentTopicNames[0] || '',
          parentTopics: parentTopicNames,
        },
        position: { x: 80, y: subtopicYPositions[subtopicName] },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
          border: `3px solid ${color}`,
          borderRadius: '16px',
          padding: '12px 18px',
          fontSize: '13px',
          fontWeight: '600',
          color: '#ffffff',
          cursor: 'pointer',
          boxShadow: `0 4px 16px ${color}66`,
          minWidth: '180px',
          textAlign: 'center',
        },
      };
      newNodes.push(subtopicNode);
    });

    // FLATTEN ALL PREREQUISITES (Levels 1 and 2)
    const allPrerequisitesMap = new Map();
    const prerequisiteRelationships = new Map();
    const subtopicToPrereqMap = new Map();

    Object.entries(subtopicData).forEach(([subtopicName, data]) => {
      if (data.prerequisites && Array.isArray(data.prerequisites)) {
        data.prerequisites.forEach(prereq => {
          const flattened = flattenPrerequisites(prereq);
          flattened.forEach(flatPrereq => {
            if (!allPrerequisitesMap.has(flatPrereq.id)) {
              allPrerequisitesMap.set(flatPrereq.id, {
                ...flatPrereq,
                connectedSubtopics: new Set()
              });
            }
            if (flatPrereq.level === 0) {
              allPrerequisitesMap.get(flatPrereq.id).connectedSubtopics.add(subtopicName);
              if (!subtopicToPrereqMap.has(subtopicName)) {
                subtopicToPrereqMap.set(subtopicName, []);
              }
              subtopicToPrereqMap.get(subtopicName).push(flatPrereq.id);
            }

            if (flatPrereq.parentId) {
              if (!prerequisiteRelationships.has(flatPrereq.parentId)) {
                prerequisiteRelationships.set(flatPrereq.parentId, []);
              }
              prerequisiteRelationships.get(flatPrereq.parentId).push(flatPrereq.id);
            }
          });
        });
      }
    });

    let levelTracker = {};
    const levelXOffset = 400;
    const prerequisitePositions = new Map();

    const sortedPrereqs = Array.from(allPrerequisitesMap.entries())
      .sort((a, b) => {
        if (a[1].level !== b[1].level) return a[1].level - b[1].level;
        return 0;
      });

    sortedPrereqs.forEach(([prereqId, prereqData]) => {
      const baseX = 400;
      const xPos = baseX + (prereqData.level * levelXOffset);

      let siblingCount = prerequisiteRelationships.get(prereqData.parentId)?.length || 1;
      let dynamicGap = 120 + (siblingCount * 30);

      if (levelTracker[prereqData.level] == null) {
        levelTracker[prereqData.level] = 100;
      }
      let yPos = levelTracker[prereqData.level];
      levelTracker[prereqData.level] += dynamicGap;

      prerequisitePositions.set(prereqId, { x: xPos, y: yPos });

      let prereqColor = '#4ade80';
      const connectedSubtopics = Array.from(prereqData.connectedSubtopics);
      if (connectedSubtopics.length > 0) {
        prereqColor = getSubtopicColor(connectedSubtopics[0], topicColorMap, subtopicParents);
      }

      const opacity = Math.max(0.3, 1 - (prereqData.level * 0.15));
      const borderWidth = Math.max(1, 2 - prereqData.level);
      const level = prereqData.level + 1;

      const prereqNode = {
        id: `prereq_${prereqId}`,
        data: {
          label: prereqData.name,
          type: 'prerequisite',
          level: level,
          prerequisiteData: prereqData,
        },
        position: { x: xPos, y: yPos },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: `linear-gradient(135deg, ${prereqColor}${Math.round(opacity * 100).toString(16).padStart(2, '0')} 0%, ${prereqColor}${Math.round(opacity * 50).toString(16).padStart(2, '0')} 100%)`,
          border: `${borderWidth}px solid ${prereqColor}`,
          borderRadius: '12px',
          padding: prereqData.level === 0 ? '10px 16px' : '8px 12px',
          fontSize: prereqData.level === 0 ? '12px' : '11px',
          fontWeight: '600',
          color: prereqData.level === 0 ? '#1a1a1a' : '#4a5568',
          cursor: 'pointer',
          boxShadow: `0 4px 12px ${prereqColor}${Math.round(opacity * 64).toString(16).padStart(2, '0')}`,
          minWidth: prereqData.level === 0 ? '130px' : '110px',
          textAlign: 'center',
        },
      };
      newNodes.push(prereqNode);

      // Connect Subtopic to Prerequisite
      if (prereqData.level === 0) {
        connectedSubtopics.forEach(subtopicName => {
          const subtopicId = `subtopic_${subtopicName.replace(/\s+/g, '_')}`;
          const edgeColor = getSubtopicColor(subtopicName, topicColorMap, subtopicParents);
          
          const parentTopicNames = subtopicParents[subtopicName]?.map(idx => topics[idx].topic) || [];
          const parentTopicName = parentTopicNames[0] || '';

          newEdges.push({
            id: `${subtopicId}-prereq_${prereqId}`,
            source: subtopicId,
            target: `prereq_${prereqId}`,
            type: 'custom',
            data: {
              label: `📖 ${parentTopicName || subtopicName}`,
              labelColor: edgeColor,
            },
            style: {
              stroke: edgeColor,
              strokeWidth: 2,
              strokeDasharray: '5,5',
            },
          });
        });
      }
    });

    // Connect Prerequisites to Pre-prerequisites
    prerequisiteRelationships.forEach((childIds, parentId) => {
      childIds.forEach(childId => {
        const parentData = allPrerequisitesMap.get(parentId);
        let edgeColor = '#94a3b8';
        let labelText = '→';

        if (parentData && parentData.connectedSubtopics.size > 0) {
          const firstSubtopic = Array.from(parentData.connectedSubtopics)[0];
          edgeColor = getSubtopicColor(firstSubtopic, topicColorMap, subtopicParents);
          
          const parentPrereq = allPrerequisitesMap.get(parentId);
          const childPrereq = allPrerequisitesMap.get(childId);
          
          if (parentPrereq && childPrereq) {
            labelText = `${parentPrereq.name} → ${childPrereq.name}`;
          }
        }

        newEdges.push({
          id: `prereq_${parentId}-prereq_${childId}`,
          source: `prereq_${parentId}`,
          target: `prereq_${childId}`,
          type: 'custom',
          data: {
            label: labelText.length > 30 ? labelText.substring(0, 27) + '...' : labelText,
            labelColor: edgeColor,
          },
          style: {
            stroke: edgeColor,
            strokeWidth: 1.5,
            strokeDasharray: '3,3',
          },
        });
      });
    });

    setAllNodes(newNodes);
    setAllEdges(newEdges);
    
    setExpandedNodeId(null);
    setExpansionHistory([]);
    setShowFullView(true);
    setSelectedNode(null);
    
    const layoutedNodes = calculateNonOverlappingPositions(newNodes, newEdges);
    setNodes(layoutedNodes);
    setEdges(newEdges);

    setTimeout(() => fitView({ padding: 0.3, duration: 400 }), 100);
  };

  // ============================================================
  // NODE CLICK HANDLERS
  // ============================================================

  const handleCollapsibleNodeClick = (event, node) => {
    // If clicking on the expanded node, collapse it
    if (expandedNodeId === node.id) {
      const newHistory = [...expansionHistory];
      const previousNode = newHistory.pop();
      setExpandedNodeId(previousNode || null);
      setExpansionHistory(newHistory);
      
      updateCollapsibleView(previousNode || null, newHistory);
      return;
    }

    // Check if this node has direct children
    const hasChildren = allEdges.some(edge => edge.source === node.id);
    if (!hasChildren) {
      showNodeDetails(node);
      return;
    }

    // Expand this node - show ONLY its direct children
    const newHistory = [...expansionHistory];
    if (expandedNodeId) {
      newHistory.push(expandedNodeId);
    }
    
    setExpandedNodeId(node.id);
    setExpansionHistory(newHistory);
    setShowFullView(false);
    
    updateCollapsibleView(node.id, newHistory);
    showNodeDetails(node);
  };

  const handleOriginalNodeClick = (event, node) => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );

    if (!currentData) return;

    if (node.data.type === 'subtopic') {
      setSelectedNode(node.id);
      setShowFullView(false);

      const parentIndices = node.data.parents || [];
      const subtopicName = node.data.label.replace(/ \(.*\)$/, '');

      const subtopicDataMap = {};
      currentData.topics.forEach((topic) => {
        topic.subtopics?.forEach((st) => {
          subtopicDataMap[st.subtopic] = st;
        });
      });

      const currentSubtopicData = subtopicDataMap[subtopicName];

      const allPrerequisiteIds = new Set();

      if (currentSubtopicData?.prerequisites) {
        const collectPrerequisiteIds = (prereqList) => {
          prereqList.forEach(prereq => {
            if (typeof prereq === 'object' && prereq.name) {
              const flattened = flattenPrerequisites(prereq);
              flattened.forEach(flatPrereq => {
                allPrerequisiteIds.add(flatPrereq.id);
              });
            }
          });
        };

        collectPrerequisiteIds(currentSubtopicData.prerequisites);
      }

      allEdges.forEach(edge => {
        if (edge.source === node.id && edge.target.startsWith('prereq_')) {
          const prereqId = edge.target.replace('prereq_', '');
          allPrerequisiteIds.add(prereqId);
        }
      });

      const filteredNodes = allNodes.filter((n) => {
        if (n.id === node.id) return true;
        if (n.data.type === 'topic' && parentIndices.includes(n.data.index)) return true;
        if (n.data.type === 'prerequisite') {
          const prereqId = n.id.replace('prereq_', '');
          return allPrerequisiteIds.has(prereqId);
        }
        return false;
      });

      const visibleNodeIds = new Set(filteredNodes.map((n) => n.id));
      const filteredEdges = allEdges.filter(
        (edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
      );

      const relayoutedNodes = relayoutFilteredNodes(filteredNodes, node);

      setNodes(relayoutedNodes);
      setEdges(filteredEdges);

      const prereqNames = [];
      const allPrereqDetails = [];

      if (currentSubtopicData?.prerequisites) {
        currentSubtopicData.prerequisites.forEach(prereq => {
          if (typeof prereq === 'object' && prereq.name) {
            const flattened = flattenPrerequisites(prereq);
            prereqNames.push(...flattened.map(p => `${'  '.repeat(p.level)}${p.name}`));
            allPrereqDetails.push(...flattened);
          } else if (typeof prereq === 'string') {
            prereqNames.push(prereq);
          }
        });
      }

      setSidePanelContent({
        type: 'subtopic',
        subtopic: node.data.label,
        prerequisites: prereqNames,
        allPrerequisites: allPrereqDetails,
        parents: parentIndices.map((idx) => currentData.topics[idx].topic),
        subtopicData: currentSubtopicData,
      });

      if (isMobile || isTablet) {
        setDetailDrawerOpen(true);
      }

      setTimeout(() => fitView({ padding: 0.5, duration: 400 }), 100);

    } else if (node.data.type === 'topic') {
      const topic = node.data.topicData;
      setSidePanelContent({
        type: 'topic',
        topic: topic.topic,
        topic_prerequisites: topic.topic_prerequisites || [],
        description: topic.description || '',
        learning_material: topic.learning_material || [],
      });

      if (isMobile || isTablet) {
        setDetailDrawerOpen(true);
      }
    } else if (node.data.type === 'prerequisite') {
      setSidePanelContent({
        type: 'prerequisite',
        prerequisite: node.data.label,
        level: node.data.level || 0,
        prerequisiteData: node.data.prerequisiteData,
      });

      if (isMobile || isTablet) {
        setDetailDrawerOpen(true);
      }
    }
  };

  const handleNodeDoubleClick = (event, node) => {
    if (!isCollapsibleMode) return;
    
    event.stopPropagation();
    
    if (expandedNodeId === node.id) {
      const newHistory = [...expansionHistory];
      const previousNode = newHistory.pop();
      setExpandedNodeId(previousNode || null);
      setExpansionHistory(newHistory);
      
      updateCollapsibleView(previousNode || null, newHistory);
    } else if (expansionHistory.includes(node.id)) {
      const index = expansionHistory.indexOf(node.id);
      const newHistory = expansionHistory.slice(0, index);
      setExpandedNodeId(node.id);
      setExpansionHistory(newHistory);
      
      updateCollapsibleView(node.id, newHistory);
    }
  };

  const showNodeDetails = (node) => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );

    if (!currentData) return;

    if (node.data.type === 'subtopic') {
      const parentIndices = node.data.parents || [];
      const subtopicName = node.data.label.replace(/ \(.*\)$/, '');

      const subtopicDataMap = {};
      currentData.topics.forEach((topic) => {
        topic.subtopics?.forEach((st) => {
          subtopicDataMap[st.subtopic] = st;
        });
      });

      const currentSubtopicData = subtopicDataMap[subtopicName];
      const prereqNames = [];

      if (currentSubtopicData?.prerequisites) {
        currentSubtopicData.prerequisites.forEach(prereq => {
          if (typeof prereq === 'object' && prereq.name) {
            const flattened = flattenPrerequisites(prereq);
            prereqNames.push(...flattened.map(p => `${'  '.repeat(p.level)}${p.name}`));
          } else if (typeof prereq === 'string') {
            prereqNames.push(prereq);
          }
        });
      }

      setSidePanelContent({
        type: 'subtopic',
        subtopic: subtopicName,
        prerequisites: prereqNames,
        parents: parentIndices.map((idx) => currentData.topics[idx].topic),
        subtopicData: currentSubtopicData,
      });

    } else if (node.data.type === 'topic') {
      const topic = node.data.topicData;
      setSidePanelContent({
        type: 'topic',
        topic: topic.topic,
        topic_prerequisites: topic.topic_prerequisites || [],
        description: topic.description || '',
        learning_material: topic.learning_material || [],
      });
    } else if (node.data.type === 'prerequisite') {
      setSidePanelContent({
        type: 'prerequisite',
        prerequisite: node.data.label,
        level: node.data.level || 0,
        prerequisiteData: node.data.prerequisiteData,
      });
    }

    if (isMobile || isTablet) {
      setDetailDrawerOpen(true);
    }
  };

  const handleViewComplete = () => {
    if (isCollapsibleMode) {
      setExpandedNodeId(null);
      setExpansionHistory([]);
      setShowFullView(true);
      setSelectedNode(null);
      setSidePanelContent(null);
      
      const layoutedNodes = relayoutAllNodes(allNodes);
      setNodes(layoutedNodes);
      setEdges(allEdges);
      
      setTimeout(() => fitView({ padding: 0.3, duration: 400 }), 100);
    } else {
      const relayoutedAllNodes = relayoutAllNodes(allNodes);
      setNodes(relayoutedAllNodes);
      setEdges(allEdges);
      setShowFullView(true);
      setSelectedNode(null);
      setSidePanelContent(null);
      setTimeout(() => fitView({ padding: 0.3, duration: 400 }), 100);
    }
  };

  // ============================================================
  // MODE TOGGLE
  // ============================================================

  const handleModeToggle = () => {
    const newMode = !isCollapsibleMode;
    setIsCollapsibleMode(newMode);
    
    if (newMode) {
      // Switching to collapsible mode
      buildCollapsibleFlow();
    } else {
      // Switching to Full View mode
      if (allNodes.length > 0) {
        const layoutedNodes = calculateNonOverlappingPositions(allNodes, allEdges);
        setNodes(layoutedNodes);
        setEdges(allEdges);
        setShowFullView(true);
        setExpandedNodeId(null);
        setExpansionHistory([]);
        setTimeout(() => fitView({ padding: 0.3, duration: 400 }), 100);
      } else {
        buildFlow();
      }
    }
  };

  // ============================================================
  // MAIN HANDLERS
  // ============================================================

  const handleNodeClick = (event, node) => {
    if (isCollapsibleMode) {
      handleCollapsibleNodeClick(event, node);
    } else {
      handleOriginalNodeClick(event, node);
    }
  };

  const handleReset = () => {
    if (selectedClass && selectedSubject) {
      if (isCollapsibleMode) {
        buildCollapsibleFlow();
      } else {
        buildFlow();
      }
    }
  };

  const exportToPdf = async () => {
    await exportFlowToPdf({
      nodes,
      reactFlowWrapper,
      fileName: `knowledge-map-${selectedClass}-${selectedSubject}-${new Date().toISOString().split('T')[0]}.pdf`,
      backgroundColor: '#ffffff',
      rowsPerPage: 4,
      rowGap: 150,
      padding: 100,
      minHeight: 400,
      pixelRatio: 2.5,
      onProgress: (message, type) => showSnackBar(message, type),
      onError: (message, type) => showSnackBar(message, type),
      onSuccess: (message, type) => showSnackBar(message, type),
    });
    handleDownloadClose();
  };

  const exportAsTablePdf = () => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );
    exportTableToPdf({
      data: currentData,
      fileName: `knowledge-map-table-${selectedClass}-${selectedSubject}-${new Date().toISOString().split('T')[0]}.pdf`,
      onProgress: (message, type) => showSnackBar(message, type),
      onError: (message, type) => showSnackBar(message, type),
      onSuccess: (message, type) => showSnackBar(message, type),
    });
    handleDownloadClose();
  };

 const SidebarContent = () => (
  <Box sx={{ 
    height: '100%', 
    display: 'flex', 
    flexDirection: 'column',
    gap: 1.5,
    p: 1.5,
  }}>
    {error && (
      <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
        {error}
      </Alert>
    )}

    {/* Mode Toggle */}
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        borderRadius: 2, 
        border: '1px solid #e2e8f0',
        bgcolor: isCollapsibleMode ? '#f0f7ff' : '#f8fafc',
        transition: 'all 0.3s ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: isCollapsibleMode ? 'primary.main' : 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isCollapsibleMode ? '#fff' : 'grey.600',
              transition: 'all 0.3s ease',
            }}
          >
            {isCollapsibleMode ? (
              <AccountTree fontSize="small" />
            ) : (
              <Layers fontSize="small" />
            )}
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {isCollapsibleMode ? 'Collapsible Mode' : 'Full View Mode'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {isCollapsibleMode 
                ? 'Click to expand/collapse · Double-click to collapse' 
                : 'Shows subtopics with parent topics'}
            </Typography>
          </Box>
        </Box>
        <Switch
          checked={isCollapsibleMode}
          onChange={handleModeToggle}
          size="medium"
          color="primary"
        />
      </Box>
    </Paper>

    {/* Organisation Dropdown */}
    {hasClassListPermission && (
      <Box>
        <OrganisationDropdown
          value={selectedOrganisation}
          onChange={(val) => {
            setSelectedOrganisation(val);
            setSelectedClass('');
            setSelectedSubject('');
          }}
        />
      </Box>
    )}

    {/* Class Selection */}
    {hasClassListPermission ? (
      <Box>
        <SearchableDropdown
          label="Select Class"
          options={availableClasses.map((cls) => ({
            label: `Class ${cls}`,
            value: cls,
          }))}
          value={selectedClass ? { label: `Class ${selectedClass}`, value: selectedClass } : null}
          onChange={(val) => {
            setSelectedClass(val?.value || '');
            setSelectedSubject('');
          }}
          size="small"
        />
      </Box>
    ) : (
      selectedClass && (
        <Box sx={{ 
          px: 2, 
          py: 1.5, 
          bgcolor: '#f0f4ff', 
          borderRadius: 2, 
          border: '1px solid #c7d2fe',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Box sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            bgcolor: '#4338ca',
            flexShrink: 0,
          }} />
          <Typography sx={{ fontSize: 14, color: '#4338ca', fontWeight: 600 }}>
            Class {selectedClass}
          </Typography>
        </Box>
      )
    )}

    {/* Subject Selection */}
    <Box>
      <SearchableDropdown
        label="Select Subject"
        options={availableSubjects.map((sub) => ({
          label: sub,
          value: sub,
        }))}
        value={selectedSubject ? { label: selectedSubject, value: selectedSubject } : null}
        onChange={(val) => setSelectedSubject(val?.value || '')}
        disabled={!selectedClass}
        size="small"
      />
    </Box>

    {/* Action Buttons - Inline Edit/Delete */}
    {nodes.length > 0 && (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
        {!showFullView && isCollapsibleMode && (
          <Tooltip title="View Complete Map">
            <Button
              onClick={handleViewComplete}
              variant="contained"
              size="small"
              startIcon={<Visibility />}
              sx={{ 
                flex: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.75rem',
                minWidth: 0,
                py: 0.8,
              }}
            >
              Full View
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Reset View">
          <IconButton onClick={handleReset} color="primary" size="small" sx={{ borderRadius: 2 }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export as PDF">
          <IconButton onClick={(e) => handleDownloadOpen(e, 'current')} color="primary" size="small" sx={{ borderRadius: 2 }}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {/* Edit Button - Inline */}
        {constantActions.userHasPermission([globalpermissions?.performancePermissions?.knowledgeMap]) && (
          <Tooltip title="Edit Knowledge Map">
            <Button
              onClick={handleEdit}
              variant="outlined"
              size="small"
              color="primary"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.7rem',
                minWidth: 0,
                py: 0.6,
                px: 1.2,
              }}
              startIcon={<span style={{ fontSize: '14px' }}>✏️</span>}
            >
              Edit
            </Button>
          </Tooltip>
        )}
        
        {/* Delete Button - Inline */}
        {constantActions.userHasPermission([globalpermissions?.performancePermissions?.deleteKnowledgeMap]) && (
          <Tooltip title="Delete Knowledge Map">
            <Button
              onClick={handleDelete}
              variant="outlined"
              size="small"
              color="error"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.7rem',
                minWidth: 0,
                py: 0.6,
                px: 1.2,
              }}
              startIcon={<span style={{ fontSize: '14px' }}>🗑️</span>}
            >
              Delete
            </Button>
          </Tooltip>
        )}
      </Box>
    )}

    {/* Create Button */}
    {constantActions.userHasPermission([globalpermissions?.performancePermissions?.knowledgeMap]) && (
      <Button
        variant="contained"
        fullWidth
        onClick={() => navigate('/r/student/create')}
        size="medium"
        sx={{ 
          mt: 0.5,
          py: 1.2,
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
        }}
        startIcon={<AccountTree />}
      >
        Create Knowledge Map
      </Button>
    )}

    {/* Side Panel Content */}
    {sidePanelContent && (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          borderRadius: 2, 
          border: '1px solid #e2e8f0',
          bgcolor: '#fafbfc',
          flex: '1 1 auto',
          overflowY: 'auto',
          minHeight: 100,
        }}
      >
        {sidePanelContent.type === 'topic' && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
              📚 {sidePanelContent.topic}
            </Typography>
            <Divider sx={{ my: 1.5 }} />

            {sidePanelContent.description && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', display: 'block', mb: 0.5 }}>
                  ℹ️ Description
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5, fontSize: '0.85rem' }}>
                  {sidePanelContent.description}
                </Typography>
              </>
            )}

            {sidePanelContent.topic_prerequisites?.length > 0 && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', display: 'block', mb: 0.5 }}>
                  ✅ Topic Prerequisites
                </Typography>
                <List dense disablePadding>
                  {sidePanelContent.topic_prerequisites.map((p, i) => (
                    <ListItem key={i} sx={{ pl: 0, py: 0.3 }}>
                      <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem' }}>
                        • {p}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}

        {sidePanelContent.type === 'subtopic' && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
              📝 {sidePanelContent.subtopic}
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            
            {sidePanelContent.parents && sidePanelContent.parents.length > 0 && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', display: 'block', mb: 0.5 }}>
                  📖 Part of Topics
                </Typography>
                <List dense disablePadding>
                  {sidePanelContent.parents.map((p, i) => (
                    <ListItem key={i} sx={{ pl: 0, py: 0.3 }}>
                      <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem' }}>
                        • {p}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {sidePanelContent.prerequisites && sidePanelContent.prerequisites.length > 0 && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', display: 'block', mt: 1, mb: 0.5 }}>
                  ✅ Prerequisites
                </Typography>
                <List dense disablePadding>
                  {sidePanelContent.prerequisites.map((p, i) => (
                    <ListItem key={i} sx={{ pl: 0, py: 0.3 }}>
                      <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                        {p}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}

        {sidePanelContent.type === 'prerequisite' && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#475569', mb: 0.5 }}>
              ✅ {sidePanelContent.prerequisite}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem' }}>
              {sidePanelContent.level >= 4 
                ? 'Foundational pre-prerequisite concept required for understanding higher-level prerequisites.'
                : 'Prerequisite concept required for understanding related subtopics.'}
            </Typography>
            {sidePanelContent.level && (
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>
                Level: {sidePanelContent.level === 3 ? 'Prerequisite' : 'Pre-Prerequisite'}
              </Typography>
            )}
          </>
        )}
      </Paper>
    )}

    {/* Empty state */}
    {!sidePanelContent && nodes.length > 0 && (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          borderRadius: 2, 
          border: '1px solid #e2e8f0',
          bgcolor: '#fafbfc',
          flex: '1 1 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 100,
        }}
      >
        <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center' }}>
          {isCollapsibleMode ? 'Click on a topic to expand' : 'Click on a subtopic to view its prerequisites'}
        </Typography>
      </Paper>
    )}
  </Box>
);


  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { sm: 'row', xs: 'column' }, 
      height: { sm: '100vh', xs: '100%' }, 
      bgcolor: '#f8fafc',
      overflow: 'hidden',
    }}>
      {/* Flow Container */}
      <Box ref={reactFlowWrapper} sx={{
        flex: 1,
        width: { sm: '70%', xs: '100%' },
        height: { sm: '100%', xs: '60vh' },
        position: 'relative',
        bgcolor: '#f8fafc',
      }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodeClick={handleNodeClick}
          onNodeDoubleClick={handleNodeDoubleClick}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          fitView={false}
          nodesDraggable={false}
          style={{ background: '#f8fafc' }}
          minZoom={useCompactZoom ? 0.1 : 0.01}
          maxZoom={useCompactZoom ? 2 : 8}
          edgeTypes={{
            custom: CustomEdge,
          }}
        >
          <Controls
            style={{ button: { background: 'white', borderColor: '#e2e8f0' } }}
            position={isMobile ? 'bottom-right' : 'top-right'}
            showInteractive={!isMobile}
          />
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#cbd5e1" />
        </ReactFlow>

        {/* Menus */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              minWidth: 150,
            },
          }}
        >
          {constantActions.userHasPermission([globalpermissions?.performancePermissions?.knowledgeMap]) && (
            <MenuItem onClick={handleEdit} sx={{ gap: 1, py: 1.2 }}>
              ✏️ Edit
            </MenuItem>
          )}
          {constantActions.userHasPermission([globalpermissions?.performancePermissions?.deleteKnowledgeMap]) && (
            <MenuItem onClick={handleDelete} sx={{ gap: 1, py: 1.2, color: 'error.main' }}>
              🗑️ Delete
            </MenuItem>
          )}
        </Menu>

        <Menu
          anchorEl={anchorEl1}
          open={Boolean(anchorEl1)}
          onClose={handleDownloadClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              minWidth: 180,
            },
          }}
        >
          <MenuItem onClick={exportToPdf} sx={{ py: 1.2 }}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            Export graph as PDF
          </MenuItem>
          <MenuItem onClick={exportAsTablePdf} sx={{ py: 1.2 }}>
            <ListItemIcon>
              <TableChartIcon fontSize="small" />
            </ListItemIcon>
            Export table as PDF
          </MenuItem>
        </Menu>

        {/* Mobile Detail Drawer */}
        {(isMobile || isTablet) && (
          <Drawer
            anchor="bottom"
            open={detailDrawerOpen}
            onClose={() => setDetailDrawerOpen(false)}
            PaperProps={{
              sx: {
                maxHeight: '70vh',
                borderRadius: '20px 20px 0 0',
                p: 2,
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {sidePanelContent?.type === 'topic' ? 'Topic Details' : 
                 sidePanelContent?.type === 'subtopic' ? 'Subtopic Details' : 
                 sidePanelContent?.type === 'prerequisite' ? 'Prerequisite Details' : 'Details'}
              </Typography>
              <IconButton onClick={() => setDetailDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ mt: 2, overflowY: 'auto', flex: 1 }}>
              {sidePanelContent?.type === 'topic' && (
                <>
                  <Typography variant="h6">{sidePanelContent.topic}</Typography>
                  {sidePanelContent.description && (
                    <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
                      {sidePanelContent.description}
                    </Typography>
                  )}
                  {sidePanelContent.topic_prerequisites?.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
                        Prerequisites:
                      </Typography>
                      <List dense>
                        {sidePanelContent.topic_prerequisites.map((p, i) => (
                          <ListItem key={i} sx={{ pl: 0 }}>
                            <Typography variant="body2">• {p}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </>
              )}
              {sidePanelContent?.type === 'subtopic' && (
                <>
                  <Typography variant="h6">{sidePanelContent.subtopic}</Typography>
                  {sidePanelContent.parents && (
                    <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
                      Part of: {sidePanelContent.parents.join(', ')}
                    </Typography>
                  )}
                  {sidePanelContent.prerequisites?.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
                        Prerequisites:
                      </Typography>
                      <List dense>
                        {sidePanelContent.prerequisites.map((p, i) => (
                          <ListItem key={i} sx={{ pl: 0 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {p}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </>
              )}
              {sidePanelContent?.type === 'prerequisite' && (
                <>
                  <Typography variant="h6">{sidePanelContent.prerequisite}</Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
                    {sidePanelContent.level >= 4 
                      ? 'Foundational pre-prerequisite concept.'
                      : 'Prerequisite concept for related subtopics.'}
                  </Typography>
                </>
              )}
            </Box>
          </Drawer>
        )}
      </Box>

      {/* Desktop Sidebar */}
      <Box sx={{ 
        width: { sm: '30%', xs: '100%' }, 
        height: { sm: '100%', xs: 'auto' },
        overflowY: 'auto', 
        bgcolor: '#ffffff',
        borderLeft: { sm: '1px solid #e2e8f0', xs: 'none' },
        borderTop: { sm: 'none', xs: '1px solid #e2e8f0' },
        display: { xs: 'none', sm: 'block' },
        minWidth: 320,
        maxWidth: 400,
      }}>
        <SidebarContent />
      </Box>

      {/* Mobile Sidebar */}
      <Box sx={{ 
        display: { xs: 'block', sm: 'none' },
        p: 2,
        bgcolor: '#ffffff',
        borderTop: '1px solid #e2e8f0',
        maxHeight: '40vh',
        overflowY: 'auto',
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Mode Toggle - Mobile */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              border: '1px solid #e2e8f0',
              bgcolor: isCollapsibleMode ? '#f0f7ff' : '#f8fafc',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isCollapsibleMode ? (
                  <AccountTree fontSize="small" color="primary" />
                ) : (
                  <Layers fontSize="small" color="action" />
                )}
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {isCollapsibleMode ? 'Collapsible' : 'Full View'}
                </Typography>
              </Box>
              <Switch
                checked={isCollapsibleMode}
                onChange={handleModeToggle}
                size="small"
                color="primary"
              />
            </Box>
          </Paper>

          {hasClassListPermission && (
            <OrganisationDropdown
              value={selectedOrganisation}
              onChange={(val) => {
                setSelectedOrganisation(val);
                setSelectedClass('');
                setSelectedSubject('');
              }}
            />
          )}
          
          {hasClassListPermission ? (
            <SearchableDropdown
              label="Select Class"
              options={availableClasses.map((cls) => ({
                label: `Class ${cls}`,
                value: cls,
              }))}
              value={selectedClass ? { label: `Class ${selectedClass}`, value: selectedClass } : null}
              onChange={(val) => {
                setSelectedClass(val?.value || '');
                setSelectedSubject('');
              }}
              size="small"
              fullWidth
            />
          ) : (
            selectedClass && (
              <Box sx={{ 
                px: 2, 
                py: 1.2, 
                bgcolor: '#f0f4ff', 
                borderRadius: 2, 
                border: '1px solid #c7d2fe',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4338ca' }} />
                <Typography sx={{ fontSize: 13, color: '#4338ca', fontWeight: 600 }}>
                  Class {selectedClass}
                </Typography>
              </Box>
            )
          )}
          
          <SearchableDropdown
            label="Select Subject"
            options={availableSubjects.map((sub) => ({
              label: sub,
              value: sub,
            }))}
            value={selectedSubject ? { label: selectedSubject, value: selectedSubject } : null}
            onChange={(val) => setSelectedSubject(val?.value || '')}
            disabled={!selectedClass}
            size="small"
            fullWidth
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
          {constantActions.userHasPermission([globalpermissions?.performancePermissions?.knowledgeMap]) && (
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/r/student/create')}
              sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}
              startIcon={<AccountTree />}
            >
              Create
            </Button>
          )}
          {nodes.length > 0 && (
            <>
              {!showFullView && isCollapsibleMode && (
                <Button
                  onClick={handleViewComplete}
                  variant="contained"
                  size="small"
                  startIcon={<Visibility />}
                  sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Full
                </Button>
              )}
              <IconButton onClick={handleReset} color="primary" size="small" sx={{ borderRadius: 2 }}>
                <Refresh fontSize="small" />
              </IconButton>
              <IconButton onClick={(e) => handleDownloadOpen(e, 'current')} color="primary" size="small" sx={{ borderRadius: 2 }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={(e) => handleMenuOpen(e, 'current')} color="primary" size="small" sx={{ borderRadius: 2 }}>
                <MenuIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>

        {sidePanelContent && (
          <Paper elevation={0} sx={{ p: 1.5, mt: 1, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fafbfc' }}>
            {sidePanelContent.type === 'topic' && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{sidePanelContent.topic}</Typography>
                {sidePanelContent.description && (
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontSize: '0.8rem' }}>
                    {sidePanelContent.description}
                  </Typography>
                )}
              </>
            )}
            {sidePanelContent.type === 'subtopic' && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{sidePanelContent.subtopic}</Typography>
                {sidePanelContent.parents && (
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                    Topics: {sidePanelContent.parents.join(', ')}
                  </Typography>
                )}
              </>
            )}
            {sidePanelContent.type === 'prerequisite' && (
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>✅ {sidePanelContent.prerequisite}</Typography>
            )}
          </Paper>
        )}
      </Box>

      {/* Confirm Dialog */}
      {confirmOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            p: 2,
          }}
          onClick={() => setConfirmOpen(false)}
        >
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              maxWidth: { xs: '90%', sm: 400 },
              width: '100%',
              borderRadius: 3,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Delete Knowledge Map
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
              Are you sure you want to delete this knowledge map?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={() => handleConfirmDelete(false)} variant="outlined" size="small">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleConfirmDelete(true)}
                size="small"
              >
                Delete
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Hidden Table for PDF Export */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '1200px',
          zIndex: -1,
          opacity: 0,
          pointerEvents: 'none',
          backgroundColor: '#fff'
        }}
      >
        <Box ref={tableRef}>
          <KnowledgeMapTable
            data={curriculumData.find(
              (c) => c.class === selectedClass && c.subject === selectedSubject
            )}
            subject={selectedSubject}
            className={selectedClass}
            topicColors={topicColors}
            forPdf={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default function DynamicFlow() {
  return (
    <ReactFlowProvider>
      <FlowComponent />
    </ReactFlowProvider>
  );
}